export const timeSlots = [
  '07:00 AM - 08:00 AM',
  '08:00 AM - 09:00 AM',
  '09:00 AM - 10:00 AM',
  '10:00 AM - 11:00 AM',
  '11:00 AM - 12:00 PM',
  '12:00 PM - 01:00 PM',
  '01:00 PM - 02:00 PM',
  '02:00 PM - 03:00 PM',
  '03:00 PM - 04:00 PM',
  '04:00 PM - 05:00 PM',
  '05:00 PM - 06:00 PM',
  '06:00 PM - 07:00 PM',
]

export const buildHourlyTimeSlotsFromInstituteTimings = (instituteTimings) => {
  if (!Array.isArray(instituteTimings) || instituteTimings.length === 0) {
    return null
  }

  let earliest = Infinity
  let latest = -Infinity

  instituteTimings.forEach((timing) => {
    const start = timeToMinutes(timing?.start_time)
    const end = timeToMinutes(timing?.end_time)

    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return

    earliest = Math.min(earliest, start)
    latest = Math.max(latest, end)
  })

  if (!Number.isFinite(earliest) || !Number.isFinite(latest) || earliest >= latest) {
    return null
  }

  const normalizedStart = Math.floor(earliest / 60) * 60
  const normalizedEnd = Math.ceil(latest / 60) * 60
  const slots = []

  for (let cursor = normalizedStart; cursor < normalizedEnd; cursor += 60) {
    slots.push(
      `${formatTimeTo12Hour(minutesToTime(cursor))} - ${formatTimeTo12Hour(minutesToTime(cursor + 60))}`
    )
  }

  return slots.length > 0 ? slots : null
}

export const extractStartTime = (timeSlot) => timeSlot.split(' - ')[0]

export const parseTime = (timeString) => {
  const [time, modifier] = timeString.split(' ')
  const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))
  const date = new Date()
  if (modifier === 'PM' && hours !== 12) {
    date.setHours(hours + 12, minutes, 0, 0) // PM logic, except for 12 PM
  } else if (modifier === 'AM' && hours === 12) {
    date.setHours(0, minutes, 0, 0) // Handle 12 AM as midnight
  } else {
    date.setHours(hours, minutes, 0, 0) // AM logic or 12 PM logic
  }
  return date
}

export const doesTimeSlotMatchEvent = (startTime, endTime, rowTimeSlot) => {
  const [rowStart, rowEnd] = rowTimeSlot.split(' - ')

  const eventStartTime = parseTime(startTime)
  const eventEndTime = parseTime(endTime)
  const rowStartTime = parseTime(rowStart)
  const rowEndTime = parseTime(rowEnd)

  return (
    (eventStartTime < rowEndTime && eventEndTime > rowStartTime) ||
    (rowStartTime < eventEndTime && rowEndTime > eventStartTime)
  )
}

export const isEventInTimeSlot = (startTime, endTime, rowValue) => {
  return doesTimeSlotMatchEvent(startTime, endTime, rowValue)
}
// Convert 24-hour format to 12-hour AM/PM format for display
export const formatTimeTo12Hour = (time24) => {
  if (!time24 || typeof time24 !== 'string') return ''

  const [hours, minutes] = time24.split(':').map(Number)
  if (isNaN(hours) || isNaN(minutes)) return time24

  const period = hours >= 12 ? 'PM' : 'AM'
  let hours12 = hours % 12
  if (hours12 === 0) hours12 = 12 // Convert 0 to 12 for 12 AM/PM

  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`
}

export const timeToMinutes = (time) => {
  if (!time || typeof time !== 'string') return 0

  // Check if time is in 24-hour format (HH:mm)
  const twentyFourHourFormat = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/
  if (twentyFourHourFormat.test(time)) {
    const [hours, minutes] = time.split(':').map(Number)
    return hours * 60 + minutes
  }

  // Handle 12-hour format (HH:mm AM/PM)
  const [timePart, period] = time.split(' ')
  if (!timePart) return 0

  let [hours, minutes] = timePart.split(':').map(Number)
  if (isNaN(hours) || isNaN(minutes)) return 0

  if (period === 'PM' && hours !== 12) {
    hours += 12 // Convert PM to 24-hour format
  }
  if (period === 'AM' && hours === 12) {
    hours = 0 // Convert 12 AM to 00:00
  }
  return hours * 60 + minutes
}

export const formatOptions = (data, labelKey, valueKey) =>
  data.map((item) => ({label: item[labelKey], value: item[valueKey]}))

export const getLabelById = (arr, id) => arr.find((item) => item.value === id)?.label || 'Unknown'

export const getLocalizedLabelById = (arr, id, intl, fallbackKey = 'SCHEDULE.TIMETABLE.UNKNOWN') =>
  arr.find((item) => item.value === id)?.label || intl.formatMessage({id: fallbackKey})

export const dayMapping = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
}

export const getLocalizedDayMapping = (intl) => ({
  1: intl.formatMessage({id: 'DAY.MONDAY'}),
  2: intl.formatMessage({id: 'DAY.TUESDAY'}),
  3: intl.formatMessage({id: 'DAY.WEDNESDAY'}),
  4: intl.formatMessage({id: 'DAY.THURSDAY'}),
  5: intl.formatMessage({id: 'DAY.FRIDAY'}),
  6: intl.formatMessage({id: 'DAY.SATURDAY'}),
  7: intl.formatMessage({id: 'DAY.SUNDAY'}),
})

export const DAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
export const DAY_NUMBERS = [1, 2, 3, 4, 5, 6, 7]

// export const days = (intl) => [
//   {label: 'M', value: 1, name: intl.formatMessage({id: 'DAY.MONDAY'})},
//   {label: 'T', value: 2, name: intl.formatMessage({id: 'DAY.TUESDAY'})},
//   {label: 'W', value: 3, name: intl.formatMessage({id: 'DAY.WEDNESDAY'})},
//   {label: 'T', value: 4, name: intl.formatMessage({id: 'DAY.THURSDAY'})},
//   {label: 'F', value: 5, name: intl.formatMessage({id: 'DAY.FRIDAY'})},
//   {label: 'S', value: 6, name: intl.formatMessage({id: 'DAY.SATURDAY'})},
//   {label: 'S', value: 7, name: intl.formatMessage({id: 'DAY.SUNDAY'})},
// ]

// Helper function to convert minutes to a "HH:MM" string format.
export const minutesToTime = (totalMinutes) => {
  if (typeof totalMinutes !== 'number' || isNaN(totalMinutes)) return '00:00'
  const hours = Math.floor(totalMinutes / 60)
  // Round to the nearest minute to avoid seconds.
  const minutes = Math.round(totalMinutes % 60)
  const paddedHours = String(hours).padStart(2, '0')
  const paddedMinutes = String(minutes).padStart(2, '0')
  return `${paddedHours}:${paddedMinutes}`
}

export const TIMETABLE_EVENT_TYPES = {
  SLOT: 'slot', // Use this for all slots (saved or unsaved). Check constraintId to determine if saved(coming from database).
  EVENT: 'break', // USe this forsaved and unsaved, Check constraintId to determine if saved(coming from database). this is event but called break due to multiple changes in business logic
  OFF_DAY: 'off_day',
}

export const EVENT_TYPES = {
  BOTH: 'both', // everything wil have this by default
  PRACTICAL: 'practical', // we will never use this, but this type has to be in this type
}

export const TIMETABLE_CATEGORIES = {
  // new
  // TIMINGS
  TIMINGS: 1,
  // DAYS
  DAYS: 2,

  // TEACHERS
  TEACHERS: 3,

  // CLASSES
  CLASSES: 4,

  // SUBJECTS
  SUBJECT: 5,

  // SLOT
  SLOT: 7,

  // STUDENT GROUPS
  STUDENT_GROUPS: 8,

  // // old
  // SCHOOL: 1,
  // TEACHER: 2,
  // TEACHER_SPECIFIC: 3,
  // CLASS: 4,
  // VENUE: 5,
  // CLASS_SPECIFIC: 6,
  // SUBJECT: 7,
}
export const TIMETABLE_SUBCATEGORIES = {
  // new
  // TIMINGS
  INSTITUITE_TIMINGS_PER_DAY: 19,
  BREAK_TIMINGS_PER_DAY: 22,
  // DAYS
  MAXIMUN_PERIOD_PER_DAY: 5,
  OFF_DAYS_IN_A_WEEK: 6,
  // TEACHERS
  AVAILABILITY: 11,
  COURSE_ALLOCATION_TEACHER_SPECIFIC: 13,
  TEACHER_MAX_CONSECUTIVE_LECTURES: 32,
  // CLASSES
  CLASS_TEACHERS: 26,
  // SUBJECT
  CONCURRENT_SUBJECTS: 28,
  SUBJECT_VENUE: 29,
  SUBJECT_PAIRS: 30,
  // SLOT
  PRE_OCCUPIED_SLOT: 25,
  SLOT_TIMINGS: 27,

  // =========== OLD ===========
  // SCHOOL_TIMINGS: 1,
  // SCHOOL_TIMINGS_FRIDAY: 2,
  BREAK_TIMINGS: 3,
  // BREAK_TIMINGS_FRIDAY: 4,
  MAX_PERIODS_PER_DAY: 5,
  // OFF_DAY_IN_THE_WEEK: 6,
  MAX_LESSONS_PER_TEACHER_PER_DAY: 7,
  MAX_HOURS_PER_MINUTES_PER_TEACHER_PER_DAY: 8,
  // SUBJECT_PREFERENCE: 9,
  // AVAILABILITY: 11,
  // CLASS_PREFERENCE: 23,
  // COURSE_ALLOCATION: 12,
  // COURSE_ALLOCATION_TEACHER_SPECIFIC: 13,
  // BREAK_TIMINGS_PER_DAY: 22,
  // // SLOT
}

export const parseTimeToMinutes = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return 0 // Prevent errors

  const [time, modifier] = timeString.split(' ') // Ensure timeString is valid
  if (!time) return 0 // In case split fails

  const [hours, minutes] = time.split(':').map(Number)
  if (isNaN(hours) || isNaN(minutes)) return 0 // Prevent NaN cases

  let totalMinutes = hours * 60 + minutes

  if (modifier === 'PM' && hours !== 12) {
    totalMinutes += 12 * 60
  } else if (modifier === 'AM' && hours === 12) {
    totalMinutes -= 12 * 60
  }

  return totalMinutes
}

export const getUniqueValues = (key, timetableData, fallbackDays = [1, 2, 3, 4, 5]) => {
  if (key === 'day') {
    const uniqueDaysSet = new Set()
    for (const entry of timetableData) {
      const eventDay = parseInt(String(entry.day || 1))
      if (eventDay >= 1 && eventDay <= 7) {
        uniqueDaysSet.add(eventDay)
      }
    }
    if (uniqueDaysSet.size === 0) {
      return fallbackDays
    }
    return Array.from(uniqueDaysSet).sort((a, b) => a - b)
  }

  if (key === 'combinedClassName') {
    let values = [
      ...new Set(
        timetableData
          .map((entry) => {
            if (entry.combinedClassName) return entry.combinedClassName
            // Fallback to className for regular events
            return entry.className
          })
          .filter((v) => v !== null && v !== undefined && v !== '')
      ),
    ]
    return values
  }

  if (key === 'combinedGradeName') {
    let values = [
      ...new Set(
        timetableData
          .map((entry) => {
            if (entry.combinedGradeName) return entry.combinedGradeName
            // Fallback to gradeName for regular events
            return entry.gradeName
          })
          .filter((v) => v !== null && v !== undefined && v !== '')
      ),
    ]
    return values
  }

  let values = [
    ...new Set(
      timetableData
        .map((entry) => {
          if (key === 'roomName') {
            return entry.roomName || `Room ${entry.roomId}` // Use roomName if available, otherwise default
          }
          return entry[key]
        })
        .filter((v) => v !== null && v !== undefined && v !== '')
    ),
  ]
  if (key === 'roomName') {
    // Separate numeric and non-numeric room names
    const numeric = values
      .filter((v) => /^\d+$/.test(v))
      .map(Number)
      .sort((a, b) => a - b)
      .map(String)
    const nonNumeric = values.filter((v) => !/^\d+$/.test(v))
    values = [...numeric, ...nonNumeric]
  }
  return values
}

export const getValueAsDate = (time) => {
  if (!time) return undefined
  const date = new Date()
  const [hours, minutes] = time.split(':').map((part) => parseInt(part, 10))
  date.setHours(hours, minutes, 0, 0) // Set hours and minutes from the passed time string
  return date
}

export const isValidDate = (str) => {
  const date = new Date(str)
  return !isNaN(date.getTime())
}

// Helper function to convert 12-hour format to 24-hour format
export const convertTo24HourFormat = (time12Hour) => {
  if (!time12Hour) return time12Hour

  const raw = String(time12Hour).trim()
  // Already 24-hour (HH:MM / HH:MM:SS) — e.g. from <input type="time" /> (onboarding-style)
  if (!/\b(AM|PM)\b/i.test(raw)) {
    const m = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
    if (m) {
      const h = parseInt(m[1], 10)
      const min = parseInt(m[2], 10)
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
    }
  }

  // Parse the 12-hour format time (e.g., "4:00 PM" or "4:00 AM")
  const [time, period] = raw.split(' ')
  if (!period) return raw
  const [hours, minutes] = time.split(':')

  let hour24 = parseInt(hours, 10)

  if (period.toLowerCase() === 'pm' && hour24 !== 12) {
    hour24 += 12
  } else if (period.toLowerCase() === 'am' && hour24 === 12) {
    hour24 = 0
  }

  // Format as 24-hour time (e.g., "16:00")
  return `${hour24.toString().padStart(2, '0')}:${minutes}`
}

/** Normalize stored/API times to HH:mm for HTML <input type="time" /> (matches onboarding timings UX). */
export const toHtmlTimeInputValue = (time) => {
  if (time == null || time === '') return ''
  if (time instanceof Date) {
    const h = time.getHours().toString().padStart(2, '0')
    const m = time.getMinutes().toString().padStart(2, '0')
    return `${h}:${m}`
  }
  const s = String(time).trim()
  if (!s) return ''
  if (/\b(AM|PM)\b/i.test(s)) return convertTo24HourFormat(s)
  const m = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (m) {
    return `${parseInt(m[1], 10).toString().padStart(2, '0')}:${parseInt(m[2], 10)
      .toString()
      .padStart(2, '0')}`
  }
  return ''
}

// Converts 12-hour format to 24-hour format to get minutes and validates the time range
export const validateTimeRange = (startTime, endTime, use12HourFormat = true) => {
  if (!startTime || !endTime) return true

  // Convert both times to minutes for comparison
  const startMinutes = use12HourFormat
    ? convert12HourToMinutes(startTime)
    : timeToMinutesFor12h(startTime)
  const endMinutes = use12HourFormat
    ? convert12HourToMinutes(endTime)
    : timeToMinutesFor12h(endTime)

  return endMinutes > startMinutes
}

// Original timeToMinutesFor12h function (for 24-hour format)
export const timeToMinutesFor12h = (timeString) => {
  if (!timeString) return 0

  // Handle case where time might be a Date object
  if (timeString instanceof Date) {
    return timeString.getHours() * 60 + timeString.getMinutes()
  }

  // Handle 24-hour format (HH:MM)
  if (timeString.includes(':')) {
    const [hours, minutes] = timeString.split(':').map(Number)
    return hours * 60 + minutes
  }

  return 0
}

// New function for 12-hour format with AM/PM
export const convert12HourToMinutes = (time12Hour) => {
  if (!time12Hour) return 0

  // Handle Date objects (convert to string first)
  if (time12Hour instanceof Date) {
    time12Hour = time12Hour.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  }

  // Remove any extra spaces and convert to uppercase
  const cleanTime = time12Hour.trim().toUpperCase()

  // Split time and period
  const timePart = cleanTime.replace(/[AP]M$/, '').trim()
  const period = cleanTime.includes('AM') ? 'AM' : cleanTime.includes('PM') ? 'PM' : ''

  const [hours, minutes = 0] = timePart.split(':').map(Number)

  let totalHours = hours

  // Convert 12-hour to 24-hour
  if (period === 'PM' && hours !== 12) {
    totalHours = hours + 12
  } else if (period === 'AM' && hours === 12) {
    totalHours = 0
  }

  return totalHours * 60 + minutes
}

// Messages that can be used with the validation
export const TIME_VALIDATION_MESSAGES = {
  END_TIME_BEFORE_START: 'End time must be after start time',
  INVALID_START_TIME: 'Invalid start time format',
  INVALID_END_TIME: 'Invalid end time format',
}

export const convertMinutesToTime = (minutes) => {
  if (!minutes || isNaN(minutes)) return '00:00'
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${hours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}`
}

export const convertTimeToMinutes = (time) => {
  if (!time) return 0
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

export const mergeConsecutiveEvents = (events) => {
  if (!Array.isArray(events)) return []

  const sorted = [...events].sort((a, b) => {
    if (a.day !== b.day) return a.day - b.day
    if (a.teacher_id !== b.teacher_id) return a.teacher_id - b.teacher_id
    if (a.classId !== b.classId) return a.classId - b.classId
    if (a.subjectId !== b.subjectId) return a.subjectId - b.subjectId
    if (a.roomName !== b.roomName) return a.roomName.localeCompare(b.roomName)
    return timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
  })

  const merged = []

  for (const current of sorted) {
    const last = merged[merged.length - 1]
    if (
      last &&
      last.day === current.day &&
      last.teacher_id === current.teacher_id &&
      last.classId === current.classId &&
      last.subjectId === current.subjectId &&
      last.roomName === current.roomName &&
      last.endTime === current.startTime
    ) {
      // Preserve the original segments so DnD can keep exact durations per part.
      // We store the segments in chronological order.
      if (!Array.isArray(last.mergedParts) || last.mergedParts.length === 0) {
        last.mergedParts = [
          {
            id: last.id,
            startTime: last.startTime,
            endTime: last.endTime,
          },
        ]
      }
      last.mergedParts.push({
        id: current.id,
        startTime: current.startTime,
        endTime: current.endTime,
      })

      // This is a merged event - mark it and update end time
      last.endTime = current.endTime
      last.isMerged = true // Mark as merged
    } else {
      merged.push({...current})
    }
  }

  return merged
}

// ======================== CATEGORY COLORS UTILITIES ========================

/**
 * Default colors for timetable categories
 */
export const getDefaultCategoryColors = () => ({
  students: '#8E24AA', // purple
  teachers: '#7fa900', // green
  subjects: '#357CD2', // blue
  classes: '#F57F17', // orange
  venues: '#df5286', // pink
})

/**
 * Get category colors from localStorage or return defaults
 */
export const getCategoryColors = () => {
  const savedColors = localStorage.getItem('timetable_category_colors')
  if (savedColors) {
    try {
      return JSON.parse(savedColors)
    } catch (e) {
      console.error('Error parsing saved colors:', e)
    }
  }
  return getDefaultCategoryColors()
}

/**
 * Save category colors to localStorage and dispatch custom event
 */
export const setCategoryColors = (colors) => {
  try {
    localStorage.setItem('timetable_category_colors', JSON.stringify(colors))

    // Dispatch custom event to notify other components
    const event = new CustomEvent('categoryColorsChanged', {detail: colors})
    window.dispatchEvent(event)

    return true
  } catch (e) {
    console.error('Error saving colors to localStorage:', e)
    return false
  }
}

/**
 * Add event listeners for category color changes
 */
export const addCategoryColorListener = (callback) => {
  const handleStorageChange = (event) => {
    if (event.key === 'timetable_category_colors' && event.newValue) {
      try {
        const newColors = JSON.parse(event.newValue)
        callback(newColors)
      } catch (e) {
        console.error('Error parsing new colors from storage:', e)
      }
    }
  }

  const handleCustomColorChange = (event) => {
    if (event.detail) {
      callback(event.detail)
    }
  }

  // Listen for localStorage changes from other tabs/components
  window.addEventListener('storage', handleStorageChange)

  // Listen for custom events from same page
  window.addEventListener('categoryColorsChanged', handleCustomColorChange)

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange)
    window.removeEventListener('categoryColorsChanged', handleCustomColorChange)
  }
}

// ======================== TIMETABLE SELECTION SESSION CACHE ========================

const TIMETABLE_SELECTION_KEY_PREFIX = 'timetable_selection:'

/**
 * Resolve tenant identifier for selection caching.
 * We prefer localStorage TENANT_KEY when available, otherwise DEFAULT_TENANT_VALUE.
 */
export const resolveTimetableTenant = (DEFAULT_TENANT_VALUE, TENANT_KEY) => {
  try {
    const fromStorage = window.localStorage.getItem(TENANT_KEY)
    if (fromStorage && fromStorage.length > 0) {
      return fromStorage
    }
  } catch {
    // ignore and fall back
  }
  return DEFAULT_TENANT_VALUE
}

/**
 * Save current timetable selection (per tenant) into sessionStorage.
 */
export const saveTimetableSelectionToSession = (tenant, payload) => {
  if (!tenant) return
  const key = `${TIMETABLE_SELECTION_KEY_PREFIX}${tenant}`
  const data = {
    ...payload,
    savedAt: Date.now(),
  }
  try {
    window.sessionStorage.setItem(key, JSON.stringify(data))
  } catch (e) {
    // Best-effort only; ignore quota or serialization errors

    console.error('Failed to save timetable selection to sessionStorage', e)
  }
}

/**
 * Read timetable selection (per tenant) from sessionStorage.
 * Returns null if missing or invalid.
 */
export const readTimetableSelectionFromSession = (tenant) => {
  if (!tenant) return null
  const key = `${TIMETABLE_SELECTION_KEY_PREFIX}${tenant}`
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed
  } catch (e) {
    console.error('Failed to read timetable selection from sessionStorage', e)
    return null
  }
}

/**
 * Clear timetable selection cache for the given tenant (or all tenants if tenant is falsy).
 */
export const clearTimetableSelectionFromSession = (tenant) => {
  try {
    if (tenant) {
      const key = `${TIMETABLE_SELECTION_KEY_PREFIX}${tenant}`
      window.sessionStorage.removeItem(key)
      return
    }
    // Clear all timetable selection entries
    const keysToRemove = []
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i)
      if (key && key.startsWith(TIMETABLE_SELECTION_KEY_PREFIX)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach((k) => window.sessionStorage.removeItem(k))
  } catch (e) {
    console.error('Failed to clear timetable selection from sessionStorage', e)
  }
}

// Function to directly check if dark mode is active
export const isDarkMode = () => {
  // Check various potential theme indicators
  const html = document.documentElement
  const body = document.body

  // Check for dark mode classes or attributes
  return (
    html.getAttribute('data-bs-theme') === 'dark' ||
    html.classList.contains('dark-mode') ||
    body.classList.contains('dark-mode') ||
    // Check for dark background color which usually indicates dark mode
    (getComputedStyle(body).backgroundColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/) &&
      parseInt(RegExp.$1) + parseInt(RegExp.$2) + parseInt(RegExp.$3) < 382)
  )
}

/**
 * Returns the desired field order array based on the constraint subcategory.
 * @param constraintType - The current constraint type object.
 * @returns An array of strings representing the sorted field keys.
 */
export const getFieldOrder = (constraintType) => {
  // Define a default order that works for most cases
  const defaultOrder = [
    'name',
    'day',
    'classId',
    'startTime',
    'endTime',
    'start_time',
    'end_time',
    'teacherId',
    'subjectId',
    'subjectId1',
    'subjectId2',
    'roomId',
    'type',
  ]

  // If there's no constraint type, return the default
  if (!constraintType || !constraintType.id) {
    return defaultOrder
  }

  // Check the subcategory ID and return a specific order if needed
  switch (constraintType.id) {
    case TIMETABLE_SUBCATEGORIES.PRE_OCCUPIED_SLOT:
      return [
        'teacherId', // Teacher first
        'classId', // Class second
        'subjectId', // Subject third
        'day',
        'startTime',
        'endTime',
        'start_time',
        'end_time',
        'roomId',
        'type',
      ]

    // Example: Add another custom order for a different subcategory
    case TIMETABLE_SUBCATEGORIES.CONCURRENT_SUBJECTS:
      return [
        'classId', // Class is most important here
        'subjectId1', // Then the two subjects
        'subjectId2',
        'day',
        'startTime',
        'endTime',
        'start_time',
        'end_time',
        'teacherId',
        'roomId',
        'type',
        'name',
      ]

    // For any other case, use the default order
    default:
      return defaultOrder
  }
}

export function getErrorMessage(id, fallback, intl) {
  const msg = intl.formatMessage({id})
  return msg && msg !== id ? msg : fallback
}

/**
 * Creates a map of subject IDs to subject names from an array of subject options.
 * Extracts the subject name from label format: "id-name (class)"
 *
 * @param {Array} subjects - Array of subject objects with {value, label} format
 * @returns {Map<number, string>} Map of subject ID to subject name
 */
export const createSubjectsMap = (subjects) => {
  const subjectsMap = new Map()
  if (!Array.isArray(subjects)) return subjectsMap

  subjects.forEach((subject) => {
    if (subject.value && subject.label) {
      // Extract subject name from label (format: "id-name (class)")
      const match = subject.label.match(/^\d+-(.+?)\s*\(/)
      if (match) {
        subjectsMap.set(subject.value, match[1].trim())
      } else {
        // Fallback: use the full label
        subjectsMap.set(subject.value, subject.label)
      }
    }
  })

  return subjectsMap
}

/**
 * Safely parses constraint data (handles both string and object formats)
 */
export const parseConstraintData = (constraint) => {
  return typeof constraint.data === 'string' ? JSON.parse(constraint.data) : constraint.data
}

/**
 * Gets subject names with fallback
 */
export const getSubjectNames = (subjectsMap, subjectId1, subjectId2, intl) => {
  const getSubjectName = (id) =>
    subjectsMap.get(id) || `${intl.formatMessage({id: 'SCHEDULE.TIMETABLE.SUBJECT'})} ${id}`

  return {
    subject1Name: getSubjectName(subjectId1),
    subject2Name: getSubjectName(subjectId2),
  }
}

/**
 * Gets class name from classes array with fallback
 */
export const getClassName = (classes, classId, intl) => {
  const classInfo = classes.find((c) => c.value === classId)
  if (classInfo?.label) return classInfo.label
  return `${intl.formatMessage({id: 'SCHEDULE.TIMETABLE.CLASS_NAME'})} ${classId}`
}

/**
 * Combines two class names for cross-class display
 * Returns single class name if both are the same or one is missing
 */
export const getCombinedClassName = (className1, className2) => {
  if (!className1 && !className2) return null
  if (!className2 || className1 === className2) return className1
  return `${className1}/${className2}`
}

/**
 * Gets the appropriate class name for an event
 * Handles concurrent/paired events with different classes
 */
export const getClassNameForEvent = (event) => {
  // For concurrent/paired events with different classes
  if (event.className1 && event.className2 && event.className1 !== event.className2) {
    return getCombinedClassName(event.className1, event.className2)
  }
  // For same-class or regular events
  return event.className || event.className1 || null
}

/**
 * Combines two grade names for cross-grade display
 * Returns single grade name if both are the same or one is missing
 */
export const getCombinedGradeName = (gradeName1, gradeName2) => {
  if (!gradeName1 && !gradeName2) return null
  if (!gradeName2 || gradeName1 === gradeName2) return gradeName1
  return `${gradeName1}/${gradeName2}`
}

/**
 * Gets the appropriate grade name for an event
 * Handles concurrent/paired events with different grades
 */
export const getGradeNameForEvent = (event) => {
  // For concurrent/paired events with different grades
  if (event.gradeName1 && event.gradeName2 && event.gradeName1 !== event.gradeName2) {
    return getCombinedGradeName(event.gradeName1, event.gradeName2)
  }
  // For same-grade or regular events
  return event.gradeName || event.gradeName1 || null
}

/**
 * Adds constraint map entries for paired/concurrent subjects
 */
export const addConstraintMapEntries = (
  constraintMap,
  subjectId1,
  subjectId2,
  subject1Name,
  subject2Name,
  className,
  constraintId,
  type // 'concurrent' | 'paired'
) => {
  constraintMap.set(subjectId1, {
    type,
    pairedWithId: subjectId2,
    pairedWithName: subject2Name,
    pairedWithClassName: className,
    constraintId,
  })
  constraintMap.set(subjectId2, {
    type,
    pairedWithId: subjectId1,
    pairedWithName: subject1Name,
    pairedWithClassName: className,
    constraintId,
  })
}

/**
 * Extracts error message from API error response
 */
export const extractErrorMessage = (error, fallbackMessage) => {
  let errorMessage = error?.message || fallbackMessage
  if (error?.result && Array.isArray(error.result)) {
    const errorMessages = error.result
      .filter((item) => item.error && item.message)
      .map((item) => item.message)
    if (errorMessages.length > 0) {
      errorMessage = errorMessages.join('; ')
    }
  }
  return errorMessage
}

/**
 * Groups events by constraintId (for concurrent/paired events)
 */
export const groupEventsByConstraintId = (events) => {
  const constraintMap = new Map()
  events.forEach((ev) => {
    if ((ev.isConcurrent || ev.isPaired) && ev.constraintId) {
      if (!constraintMap.has(ev.constraintId)) {
        constraintMap.set(ev.constraintId, ev)
      }
    }
  })
  return constraintMap
}
